import React, { forwardRef, useCallback, useContext, useState } from 'react';
import clsx from 'clsx';
import { Avatar, makeStyles } from '@material-ui/core';
import { Lozenge } from '@redshelf/redshelf-ui';
import { ConfirmationDialog, OptionsMenu } from 'components';
import {
  LabelForUserContentItems,
  UserContentCard,
} from 'components/UserContentCards';
import { UserContentItemModifiersContext } from 'contexts';
import {
  getInitials,
  getLocalSettingsProxy,
  isoStringDisplay,
} from 'utilities';
import { getHighlightProps } from 'utilities/componentUtilities';

const useStyles = makeStyles((theme) => ({
  root: ({ ribbonProps }) => {
    if (ribbonProps) {
      return {
        borderLeft: `solid 4px ${ribbonProps}`,
      };
    }
  },

  highlight: ({ highlightProps }) => {
    if (highlightProps) {
      return {
        backgroundColor: highlightProps.background,
        outlineColor: highlightProps.border,
        outlineWidth: '2px',
        outlineStyle: 'solid',
      };
    } else {
      return null;
    }
  },

  lozenge: {
    margin: theme.spacing(0.5, 0, 1.5, 0),
  },

  noteContainer: {
    display: 'flex',
    paddingTop: theme.spacing(2),
  },

  noteAvatarContainer: {
    opacity: 1,
    padding: theme.spacing(0.25),
    marginRight: theme.spacing(1),
  },

  learningToolsUserText: {
    ...theme.typography.body2,
    color: theme.palette.greyBlue[700],
    maxHeight: theme.spacing(13.125), // line height 21 * 5
    overflowY: 'auto',
    paddingRight: theme.spacing(2),
    wordBreak: 'break-word',
  },

  railTitle: {
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.pxToRem(14),
    letterSpacing: theme.typography.pxToRem(0.15),
  },

  userText: {
    ...theme.typography.body1,
    letterSpacing: theme.typography.pxToRem(0.15),
    maxHeight: theme.spacing(15),
    overflowY: 'auto',
    paddingRight: theme.spacing(2),
    wordBreak: 'break-word',
  },

  userAvatar: {
    color: theme.palette.greyBlue[700],
    backgroundColor: theme.palette.shelfBlue[50],
  },

  sharedAvatar: {
    color: theme.palette.greyBlue[700],
    backgroundColor: theme.palette.status.red.light,
  },
}));

const NoteCardConfirmationDialog = ({
  deleteNote,
  isDeleteConfirmOpen,
  setIsDeleteConfirmOpen,
  titleComponent,
}) => {
  return (
    <ConfirmationDialog
      cancel={{ label: 'Cancel', action: () => setIsDeleteConfirmOpen(false) }}
      confirm={{
        label: 'Delete',
        action: () => {
          deleteNote();
          setIsDeleteConfirmOpen(false);
        },
      }}
      id={'confirmDeleteNoteDialog'}
      isOpen={isDeleteConfirmOpen}
      labelledById="note-delete-confirmation"
      message="Deleting the note will permanently remove it from this material."
      title="Are you sure you want to proceed?"
      titleComponent={titleComponent}
    />
  );
};

const UserAvatar = ({ initials, isCollaborationContent }) => {
  const classes = useStyles();

  const avatarClass = isCollaborationContent
    ? classes.sharedAvatar
    : classes.userAvatar;

  return (
    <Avatar className={clsx(avatarClass, 'noTranslate')}>{initials}</Avatar>
  );
};

const NoteCard = (
  {
    cardId,
    className,
    isCollaborationContent,
    isLearningToolsMenu,
    onEdit,
    title,
    titleComponentForDeleteDialog,
    userContentItem,
    ...props
  },
  ref,
) => {
  const {
    bookLocation,
    color,
    dateCreated,
    truncatedSnippet,
    userFirstName,
    userLastName,
    userText,
  } = userContentItem;

  const {
    focusInlineHighlight,
    onDialogOpen,
    setNoteToEdit,
    setNoteToDisplayAsDialog,
    setAriaLiveMessageForUserContent,
  } = useContext(UserContentItemModifiersContext);
  const [localSettings] = useState(getLocalSettingsProxy);
  const paletteId = localSettings.appStyling.colorPalette;
  const propsForHighlight = getHighlightProps({ color, paletteId });
  const highlightProps =
    isLearningToolsMenu && !isCollaborationContent ? propsForHighlight : null;
  const ribbonProps =
    !isLearningToolsMenu && !isCollaborationContent
      ? propsForHighlight.background
      : null;

  const classes = useStyles({
    highlightProps,
    ribbonProps,
  });

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const editOnClick = useCallback(() => {
    onEdit();
    onDialogOpen('noteEditor');
    setNoteToEdit(userContentItem);
  }, [onEdit, onDialogOpen, setNoteToEdit, userContentItem]);
  const deleteOnClick = useCallback(() => {
    setIsDeleteConfirmOpen(true);
  }, [setIsDeleteConfirmOpen]);

  const optionsMenuItemsList = [
    { displayText: 'Edit Note', onClick: editOnClick, key: 'edit-note' },
    {
      displayText: 'Delete Note',
      onClick: deleteOnClick,
      key: 'delete-note',
    },
  ];

  const noteInitials = getInitials([userFirstName, userLastName]);

  const initials = isCollaborationContent
    ? noteInitials || 'SN'
    : noteInitials || 'MN';
  const lozenge = isCollaborationContent ? (
    <Lozenge size="small" color="error" className={classes.lozenge}>
      Shared Note
    </Lozenge>
  ) : null;
  const subheader = (
    <span className="noTranslate">{isoStringDisplay(dateCreated)}</span>
  );

  return (
    <>
      <UserContentCard
        action={
          !isCollaborationContent && (
            <OptionsMenu
              cardId={cardId}
              menuItemsList={optionsMenuItemsList}
              userContentItem={userContentItem}
            />
          )
        }
        avatar={
          !isLearningToolsMenu ? (
            <UserAvatar
              initials={initials}
              isCollaborationContent={isCollaborationContent}
            />
          ) : null
        }
        bookLocation={bookLocation}
        classNames={{
          root: clsx(classes.root, className),
          highlight: classes.highlight,
        }}
        id={cardId}
        lozenge={lozenge}
        linkLabel={
          <LabelForUserContentItems userContentItem={userContentItem} />
        }
        onSnippetClick={() => focusInlineHighlight(userContentItem)}
        ref={ref}
        snippet={isLearningToolsMenu ? truncatedSnippet : null}
        subheader={subheader}
        title={
          isLearningToolsMenu ? (
            title
          ) : (
            <p
              className={clsx(
                classes.railTitle,
                noteInitials ? 'noTranslate' : null,
              )}
            >
              {title}
            </p>
          )
        }
        {...props}
      >
        {isLearningToolsMenu ? (
          <div className={classes.noteContainer}>
            <div className={classes.noteAvatarContainer}>
              <UserAvatar
                initials={initials}
                isCollaborationContent={isCollaborationContent}
              />
            </div>
            <div className={`${classes.learningToolsUserText} noTranslate`}>
              {userText}
            </div>
          </div>
        ) : (
          <div className={`${classes.userText} noTranslate`}>{userText}</div>
        )}
      </UserContentCard>
      <NoteCardConfirmationDialog
        deleteNote={async () => {
          // Find the element to focus before deletion
          const currentCard = ref?.current;
          let elementToFocus = null;

          if (currentCard) {
            // Get the parent <li> element
            const listItem = currentCard.closest('li');
            if (listItem) {
              // Only set focus if this is the last element in the list
              if (
                !listItem.nextElementSibling &&
                listItem.previousElementSibling
              ) {
                // Find the focusable element within the previous sibling
                elementToFocus =
                  listItem.previousElementSibling.querySelector('[tabindex]') ||
                  listItem.previousElementSibling.querySelector('button');
              }
            }
          }

          setNoteToEdit(null);
          setNoteToDisplayAsDialog(null);
          await userContentItem.delete();

          // Focus the element after deletion
          if (elementToFocus) {
            elementToFocus.focus();
          }

          setTimeout(() => {
            setAriaLiveMessageForUserContent({
              action: 'deleted',
              color: userContentItem.color,
              snippet: userContentItem.snippet,
              type: userContentItem.constructor.typeName,
            });
          }, 2000);
        }}
        isDeleteConfirmOpen={isDeleteConfirmOpen}
        setIsDeleteConfirmOpen={setIsDeleteConfirmOpen}
        titleComponent={titleComponentForDeleteDialog}
      />
    </>
  );
};

// No PropTypes for forwardRef component
export default forwardRef(NoteCard);
